import { useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { axiosInstance } from 'korint-sdk';
import { useKorintConfig } from 'korint-sdk/react';

type StreamChunk =
  | { type: 'message_id'; id: string }
  | { type: 'text_delta'; text: string }
  | {
      type: 'tool_call';
      toolName: string;
      state: 'started' | 'completed' | 'error';
      args?: Record<string, unknown>;
    }
  | { type: 'done'; messageId: string; spanId?: string }
  | { type: 'error'; error: string };

interface StreamCallbacks {
  onMessageId?: (id: string) => void;
  onTextDelta?: (text: string) => void;
  onToolCall?: (
    toolName: string,
    state: 'started' | 'completed' | 'error',
    args?: Record<string, unknown>,
  ) => void;
  onDone?: (messageId: string, spanId?: string) => void;
  onError?: (error: string, isTimeout?: boolean) => void;
}

// Sized just past the backend per-call timeout (120s) so a backend-emitted error
// reaches us first; this acts as a fallback if the backend hangs without responding.
const REQUEST_TIMEOUT_MS = 125_000;

export function useConversationStream() {
  const abortControllerRef = useRef<AbortController | null>(null);
  const { clientOptions } = useKorintConfig();
  const queryClient = useQueryClient();

  const streamMessage = useCallback(
    async (
      conversationId: string,
      content: string,
      callbacks: StreamCallbacks,
      metadata?: Record<string, unknown>,
    ) => {
      abortControllerRef.current?.abort();
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      // No AbortSignal.timeout/any: they need Safari 16 / 17.4
      let timedOut = false;
      const timeoutId = setTimeout(() => {
        timedOut = true;
        abortController.abort();
      }, REQUEST_TIMEOUT_MS);

      try {
        const baseURL =
          import.meta.env.VITE_KORINT_STREAM_URL || axiosInstance.defaults.baseURL || '';
        const url = `${baseURL}/conversations/${conversationId}/messages/stream`;

        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        // Read the cached Cognito token from useAddBearerTokenInAxios's React Query cache
        const cachedToken = queryClient.getQueryData<string | null>(['token', null]);
        if (cachedToken) {
          headers.Authorization = `Bearer ${cachedToken}`;
        }
        if (clientOptions.tenant) {
          headers.Tenant = clientOptions.tenant;
        }

        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const response = await fetch(url, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            content,
            ...(metadata ? { metadata } : {}),
            ...(timezone ? { timezone } : {}),
          }),
          signal: abortController.signal,
        });

        if (!response.ok) {
          const errorText = await response.text();
          callbacks.onError?.(errorText || `HTTP ${response.status}`);
          return;
        }

        const reader = response.body?.getReader();
        if (!reader) {
          callbacks.onError?.('No response body');
          return;
        }

        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() ?? '';

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            const jsonStr = line.slice(6);
            try {
              const chunk: StreamChunk = JSON.parse(jsonStr);
              switch (chunk.type) {
                case 'message_id':
                  callbacks.onMessageId?.(chunk.id);
                  break;
                case 'text_delta':
                  callbacks.onTextDelta?.(chunk.text);
                  break;
                case 'tool_call':
                  callbacks.onToolCall?.(chunk.toolName, chunk.state ?? 'started', chunk.args);
                  break;
                case 'done':
                  callbacks.onDone?.(chunk.messageId, chunk.spanId);
                  break;
                case 'error':
                  callbacks.onError?.(chunk.error);
                  break;
              }
            } catch {
              // Unparseable lines (heartbeats, etc.)
            }
          }
        }
      } catch (error) {
        // User-triggered cancel: stay silent. Timeout: surface with isTimeout flag.
        if (abortController.signal.aborted && !timedOut) return;
        callbacks.onError?.((error as Error).message, timedOut);
      } finally {
        clearTimeout(timeoutId);
      }
    },
    [clientOptions.tenant, queryClient],
  );

  const abort = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
  }, []);

  return { streamMessage, abort };
}
